/**
 * CAN-040: Merge duplicate candidates wizard.
 * Shown when the user selects exactly 2 candidates and clicks "Merge duplicates".
 */
import { useState } from 'react';
import { ArrowRight, Loader2, GitMerge } from 'lucide-react';
import { candidateService } from '@core/supabase/services/candidateService';
import type { Candidate } from '@core/types';

interface CandidateMergeModalProps {
  orgId: string;
  orgUserId: string | null;
  candidateA: Candidate;
  candidateB: Candidate;
  onClose: () => void;
  onMerged: (survivorId: string, removedId: string) => void;
}

export function CandidateMergeModal({
  orgId,
  orgUserId,
  candidateA,
  candidateB,
  onClose,
  onMerged,
}: CandidateMergeModalProps) {
  const [primary, setPrimary] = useState<'a' | 'b'>('a');
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');

  const survivor = primary === 'a' ? candidateA : candidateB;
  const removed  = primary === 'a' ? candidateB : candidateA;

  const handleMerge = async () => {
    setMerging(true);
    setError('');
    try {
      await candidateService.merge(
        orgId,
        survivor.id,
        removed.id,
        orgUserId,
        {
          merged_name:    removed.full_name,
          merged_email:   removed.email,
          merged_phone:   removed.phone,
          merged_company: removed.current_company,
          merged_title:   removed.current_title,
        }
      );
      onMerged(survivor.id, removed.id);
    } catch (e) {
      setError((e as Error).message ?? 'Merge failed');
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-xl border border-gray-700 bg-gray-950 shadow-2xl overflow-hidden">
        <div
          className="flex items-center gap-2.5 px-5 py-4 rounded-t-xl text-white border-b vscode-border"
          style={{ backgroundColor: 'var(--brand-popup-header)' }}
        >
          <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md bg-white/15 [&_svg]:w-5 [&_svg]:h-5 text-white">
            <GitMerge className="w-5 h-5" />
          </span>
          <h2 className="text-base font-semibold text-white">Merge duplicate candidates</h2>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm vscode-text-secondary">
            Choose which record to keep as the <strong className="text-white">primary</strong>. All applications from the
            other record will be re-assigned here, then the secondary record will be archived.
          </p>

          {/* Primary / secondary picker */}
          <div className="grid grid-cols-2 gap-3">
            {(['a', 'b'] as const).map((side) => {
              const cand = side === 'a' ? candidateA : candidateB;
              const isSelected = primary === side;
              return (
                <button
                  key={side}
                  type="button"
                  onClick={() => setPrimary(side)}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    isSelected
                      ? 'border-brand-500 bg-brand-900/20'
                      : 'border-gray-700 hover:border-gray-600 bg-gray-900/40'
                  }`}
                >
                  <p className="text-xs font-semibold mb-1 uppercase tracking-wide vscode-text-tertiary">
                    {isSelected ? '✓ Keep (primary)' : 'Merge into primary →'}
                  </p>
                  <p className="font-medium text-white">{cand.full_name}</p>
                  {cand.email    && <p className="text-xs vscode-text-secondary truncate">{cand.email}</p>}
                  {cand.phone    && <p className="text-xs vscode-text-secondary">{cand.phone}</p>}
                  {cand.current_title && <p className="text-xs vscode-text-secondary">{cand.current_title}</p>}
                </button>
              );
            })}
          </div>

          {/* Summary */}
          <div className="flex items-center gap-3 text-sm vscode-text-secondary bg-gray-900/50 rounded-lg p-3">
            <span className="text-white font-medium truncate max-w-[140px]">{removed.full_name}</span>
            <ArrowRight className="w-4 h-4 shrink-0" />
            <span className="text-white font-medium truncate max-w-[140px]">{survivor.full_name}</span>
            <span className="ml-auto vscode-text-tertiary text-xs">archived</span>
          </div>

          {error && (
            <p className="text-sm text-red-400 bg-red-950/30 border border-red-800 rounded-lg px-3 py-2">{error}</p>
          )}
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <button
            className="btn-primary"
            onClick={handleMerge}
            disabled={merging}
          >
            {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
            Confirm merge
          </button>
          <button className="btn-secondary" onClick={onClose} disabled={merging}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
